import React, { useEffect, useState } from "react";
import { api } from "../api/api";

// Cross-segment trade ledger + day/week/month P&L, backed by trade_store.py
// on the backend (a proper queryable record of every closed leg, separate
// from the free-form per-strategy event log the "Run" monitor shows).

const SEGMENT_FILTERS = [
  { value: "", label: "All Segments" },
  { value: "INDEX_OPTIONS", label: "Index Option" },
  { value: "OPTIONS", label: "Stock Option" },
  { value: "MCX", label: "MCX Commodity" },
  { value: "STOCK", label: "Stock Trade" },
];

const SEGMENT_LABEL = {
  INDEX_OPTIONS: "Index Option",
  OPTIONS: "Stock Option",
  MCX: "MCX Commodity",
  STOCK: "Stock Trade",
};

const GROUP_BYS = ["day", "week", "month"];

function pnlColor(v) {
  const n = Number(v) || 0;
  if (n > 0) return "var(--accent)";
  if (n < 0) return "var(--danger)";
  return "var(--text-dim)";
}

function isDryRun(v) {
  return v === true || v === "True" || v === "true";
}

export default function TradeHistory() {
  const [segment, setSegment] = useState("");
  const [groupBy, setGroupBy] = useState("day");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summary, setSummary] = useState({ overall: {}, periods: [] });
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set()); // trade ids showing their per-lot fill breakdown

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const refresh = () => {
    const params = { segment: segment || undefined, from: dateFrom || undefined, to: dateTo || undefined };
    setLoading(true);
    setError("");
    Promise.all([
      api.tradeSummary({ ...params, group_by: groupBy }),
      api.listTrades(params),
    ])
      .then(([s, t]) => {
        setSummary(s);
        setTrades(t);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, groupBy, dateFrom, dateTo]);

  const exportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const params = { segment: segment || undefined, from: dateFrom || undefined, to: dateTo || undefined };
      const { blob, filename } = await api.exportTrades(params);
      // Browser download via a throwaway object URL -- when this page is
      // loaded from the VPS (arpitadashboard.duckdns.org), the browser's
      // normal download mechanism already saves it straight to the local
      // machine's Downloads folder, no separate file-transfer step needed.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const overall = summary.overall || {};
  const periods = summary.periods || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select className="f-select" style={{ width: 180 }} value={segment} onChange={(e) => setSegment(e.target.value)}>
          {SEGMENT_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <div style={{ display: "flex", gap: 4 }}>
          {GROUP_BYS.map((g) => (
            <button
              key={g}
              type="button"
              className="btn btn-secondary"
              style={{
                padding: "6px 14px", fontSize: 11.5,
                ...(groupBy === g ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}),
              }}
              onClick={() => setGroupBy(g)}
            >
              {g[0].toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>

        <input type="date" className="f-input" style={{ width: 145 }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <span style={{ color: "var(--text-dim)", fontSize: 12 }}>to</span>
        <input type="date" className="f-input" style={{ width: 145 }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        {(dateFrom || dateTo) && (
          <button type="button" className="btn btn-secondary" style={{ padding: "6px 12px", fontSize: 11.5 }} onClick={() => { setDateFrom(""); setDateTo(""); }}>
            Clear dates
          </button>
        )}
        <button type="button" className="btn btn-secondary" style={{ padding: "6px 12px", fontSize: 11.5 }} onClick={refresh} disabled={loading}>
          {loading ? "..." : "Refresh"}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ padding: "6px 14px", fontSize: 11.5, marginLeft: "auto" }}
          onClick={exportExcel}
          disabled={exporting || trades.length === 0}
          title={trades.length === 0 ? "No trades to export in this range" : "Download the trades currently shown as an .xlsx file"}
        >
          {exporting ? "Exporting..." : "Export to Excel"}
        </button>
      </div>

      {error && <div className="status-banner err">{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <Stat label="Total P&L" value={(Number(overall.pnl) || 0).toFixed(2)} color={pnlColor(overall.pnl)} />
        <Stat label="Trades" value={overall.trade_count || 0} />
        <Stat label="Win Rate" value={`${overall.win_rate || 0}%`} />
        <Stat label="Wins / Losses" value={`${overall.wins || 0} / ${overall.losses || 0}`} />
      </div>

      <div className="panel">
        <div className="panel-title">P&amp;L by {groupBy}</div>
        {periods.length === 0 ? (
          <div style={{ color: "var(--text-dim)", fontSize: 12.5 }}>No closed trades in this range yet.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Period</th>
                <th style={styles.th}>P&amp;L</th>
                <th style={styles.th}>Trades</th>
                <th style={styles.th}>Win Rate</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p.period}>
                  <td style={styles.td}>{p.period}</td>
                  <td style={{ ...styles.td, color: pnlColor(p.pnl), fontFamily: "var(--font-mono)" }}>{Number(p.pnl).toFixed(2)}</td>
                  <td style={styles.td}>{p.trade_count}</td>
                  <td style={styles.td}>{p.win_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <div className="panel-title">Trade Log</div>
        {trades.length === 0 ? (
          <div style={{ color: "var(--text-dim)", fontSize: 12.5 }}>No trades yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Entry Time</th>
                  <th style={styles.th}>Exit Time</th>
                  <th style={styles.th}>Strategy</th>
                  <th style={styles.th}>Segment</th>
                  <th style={styles.th}>Symbol</th>
                  <th style={styles.th}>Side</th>
                  <th style={styles.th}>Qty</th>
                  <th style={styles.th}>Lots</th>
                  <th style={styles.th}>Entry</th>
                  <th style={styles.th}>Exit</th>
                  <th style={styles.th}>P&amp;L</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Mode</th>
                  <th style={styles.th}>Entry RSI</th>
                  <th style={styles.th}>Exit RSI</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => {
                  const fills = t.fills || [];
                  const hasBreakdown = fills.length > 1;
                  const isOpen = expanded.has(t.id);
                  return (
                    <React.Fragment key={t.id}>
                      <tr>
                        <td style={styles.td}>{t.entry_time ? new Date(t.entry_time).toLocaleString() : "—"}</td>
                        <td style={styles.td}>{t.exit_time ? new Date(t.exit_time).toLocaleString() : "—"}</td>
                        <td style={styles.td}>{t.alias_name || "(unnamed)"}</td>
                        <td style={styles.td}>{SEGMENT_LABEL[t.segment] || t.segment}</td>
                        <td style={{ ...styles.td, fontFamily: "var(--font-mono)" }}>{t.tradingsymbol}</td>
                        <td style={styles.td}>{t.side}</td>
                        <td style={styles.td}>{t.qty}</td>
                        <td style={styles.td}>
                          {hasBreakdown ? (
                            <span
                              style={{ color: "var(--focus)", cursor: "pointer", textDecoration: "underline" }}
                              onClick={() => toggleExpanded(t.id)}
                              title="Show the per-lot fill breakdown, including pyramid adds"
                            >
                              {fills.length} fills {isOpen ? "▲" : "▼"}
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-dim)" }}>1 fill</span>
                          )}
                        </td>
                        <td style={styles.td}>{Number(t.entry_price).toFixed(2)}</td>
                        <td style={styles.td}>{Number(t.exit_price).toFixed(2)}</td>
                        <td style={{ ...styles.td, color: pnlColor(t.pnl), fontFamily: "var(--font-mono)" }}>{Number(t.pnl).toFixed(2)}</td>
                        <td style={styles.td}>{t.reason}</td>
                        <td style={styles.td}>{isDryRun(t.dry_run) ? "Paper" : "Live"}</td>
                        <td style={styles.td}>{t.entry_rsi !== "" && t.entry_rsi != null ? Number(t.entry_rsi).toFixed(1) : "—"}</td>
                        <td style={styles.td}>{t.exit_rsi !== "" && t.exit_rsi != null ? Number(t.exit_rsi).toFixed(1) : "—"}</td>
                      </tr>
                      {hasBreakdown && isOpen && (
                        <tr>
                          <td colSpan={14} style={{ ...styles.td, background: "var(--panel-alt)", whiteSpace: "normal" }}>
                            {fills.map((f, i) => (
                              <span key={i} style={{ marginRight: 16, fontSize: 11.5, color: "var(--text-dim)" }}>
                                Lot {i + 1}: {f.qty}@{Number(f.price).toFixed(2)}
                                {f.rung ? ` (rung ${f.rung})` : " (initial)"}
                                {f.at ? ` · ${new Date(f.at).toLocaleTimeString()}` : ""}
                              </span>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 6, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 17, fontWeight: 600, color: color || "var(--text)" }}>{value}</div>
    </div>
  );
}

const styles = {
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", color: "var(--text-dim)", fontWeight: 500, padding: "7px 8px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" },
  td: { padding: "7px 8px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" },
};
